import Cropper from 'cropperjs';
import { useCallback } from 'react';

import type { MNImageContent, MNImageEditorDataContent } from '../../../../../../common/elements/elementModelTypes';
import { asObject } from '../../../../../../common/utils/immutableHelper';
import { DEFAULT_LOSSY_IMAGE_QUALITY } from '../utils/imageModalImageBlobUtils';
import { conformFilenameToBlobType, enrichBlobAsUploadFile } from '../../imageModalFileUtils';
import { getImageEditorScale } from '../../imageModalUtils';
import type { DrawingTransform } from '../../utils/imageDrawingTransformUtils';

type UploadImageFile = Blob & { name: string; lastModified: number };

type UseCropSaveHandlerOptions = {
    cropperRef: React.RefObject<Cropper>;
    isDirtyRef: React.MutableRefObject<boolean>;
    elementId: string;
    cropSourceImage: MNImageContent;
    imageType: string;
    largestUneditedImageUrl: string;
    hideDrawing: boolean;
    // `didSave` is true only when the save actually persisted a changed image (crop/rotate upload or a revert),
    // so the caller can gate its post-save transition on the new source loading. `viewPreviewReady` means the
    // view is already rendering the saved image (the upload's local preview), so no such gating is needed.
    afterSaveCb: (didSave?: boolean, options?: { viewPreviewReady?: boolean }) => void;
    getFilenameFromUrl: (url: string, type: string) => string;
    appendToFilename: (filename: string) => string;
    inRange: (min: number, max: number) => (value: number) => boolean;
    // `updateElementImageData` stores crop metadata before the edited upload completes so the element
    // already points at the correct source/original pair for subsequent toggles, revert, and sync.
    updateElementImageData: (
        elementId: string,
        imageOriginal: MNImageContent,
        imageEditorData: MNImageEditorDataContent,
        clearDrawing: boolean,
        drawingSessionTransform?: DrawingTransform,
    ) => void;
    // `revertToOriginalImageData` restores the full source image when the crop rectangle ends up
    // effectively matching the uncropped original again.
    revertToOriginalImageData: (elementId: string, imageOriginal: MNImageContent, clearDrawing: boolean) => void;
    uploadEditedImage: (elementId: string, file: UploadImageFile) => Promise<MNImageContent | void> | void;
    // True when the upload publishes a local preview that the modal view renders while it is in flight
    // (the plain, non background-removal V2 path). The post-save handoff then completes immediately —
    // the view already shows the saved pixels — instead of waiting for the saved source to load.
    hasOptimisticUploadPreview?: boolean;
    /** Lets V2 defer canvas work until its modal-owned saving toast has finished entering. */
    scheduleCroppedImageCreation?: (createCroppedImage: () => void) => void;
    onSaveStart?: () => void;
    shouldSaveWhenClean?: boolean;
    getPersistedImageEditorData?: (sessionData: Cropper.Data) => Cropper.Data;
    getDrawingSessionTransform?: () => DrawingTransform;
    getRevertedImage?: () => MNImageContent | null;
    createAdditionalCroppedImageBlob?: (imageEditorData: Cropper.Data) => Promise<Blob | null>;
    uploadAdditionalCroppedImage?: (options: {
        file: UploadImageFile;
        activeRenderedImage?: MNImageContent;
    }) => Promise<MNImageContent | void> | void;
};

/**
 * Persist the crop selection for the active image, and optionally mirror the same crop onto the inactive
 * background-removal variant so both toggle states stay aligned.
 */
export const useCropSaveHandler = ({
    cropperRef,
    isDirtyRef,
    elementId,
    cropSourceImage,
    imageType,
    largestUneditedImageUrl,
    hideDrawing,
    afterSaveCb,
    getFilenameFromUrl,
    appendToFilename,
    inRange,
    updateElementImageData,
    revertToOriginalImageData,
    uploadEditedImage,
    hasOptimisticUploadPreview = false,
    scheduleCroppedImageCreation,
    onSaveStart,
    shouldSaveWhenClean = false,
    getPersistedImageEditorData,
    getDrawingSessionTransform,
    getRevertedImage,
    createAdditionalCroppedImageBlob,
    uploadAdditionalCroppedImage,
}: UseCropSaveHandlerOptions) => {
    const handleSave = useCallback(() => {
        const cropper = cropperRef.current;
        if (!cropper) return;

        if (!isDirtyRef.current && !shouldSaveWhenClean) {
            afterSaveCb(false);
            return;
        }

        const imageData = cropper.getImageData();
        const sessionImageEditorData = cropper.getData();
        const imageEditorData = getPersistedImageEditorData
            ? getPersistedImageEditorData(sessionImageEditorData)
            : sessionImageEditorData;
        const originalImage = asObject<MNImageContent>(cropSourceImage);

        const inWidthRange = inRange(imageData.naturalWidth * 0.99, imageData.naturalWidth * 1.01);
        const inHeightRange = inRange(imageData.naturalHeight * 0.99, imageData.naturalHeight * 1.01);

        const shouldRevertToOriginal =
            originalImage &&
            imageEditorData.rotate === 0 &&
            getImageEditorScale(imageEditorData.scaleX) === 1 &&
            getImageEditorScale(imageEditorData.scaleY) === 1 &&
            inWidthRange(imageEditorData.width) &&
            inHeightRange(imageEditorData.height);

        if (shouldRevertToOriginal) {
            const imageToRevert = getRevertedImage?.() || asObject<MNImageContent>(originalImage);
            onSaveStart?.();
            revertToOriginalImageData(elementId, imageToRevert, hideDrawing);
            afterSaveCb(true);
            return;
        }

        const blobCreatedCb = async (blob: Blob) => {
            const originalFilename = getFilenameFromUrl(largestUneditedImageUrl, imageType);
            // Shared stem so both variants stay recognisably the same image; each one takes its own
            // extension below, since an edit can re-encode a variant into a different format.
            const imageFilename = appendToFilename(originalFilename);

            const enrichedBlob = enrichBlobAsUploadFile(blob, conformFilenameToBlobType(imageFilename, blob));

            const drawingSessionTransform = getDrawingSessionTransform?.();
            if (drawingSessionTransform) {
                updateElementImageData(elementId, originalImage, imageEditorData, hideDrawing, drawingSessionTransform);
            } else {
                updateElementImageData(elementId, originalImage, imageEditorData, hideDrawing);
            }

            try {
                const activeRenderedImage = await uploadEditedImage(elementId, enrichedBlob);

                if (createAdditionalCroppedImageBlob && uploadAdditionalCroppedImage) {
                    // Background removal keeps two variants. When the inactive side already exists,
                    // we crop it with the same selection so toggling still lines up afterwards.
                    const additionalBlob = await createAdditionalCroppedImageBlob(imageEditorData);

                    if (additionalBlob) {
                        await uploadAdditionalCroppedImage({
                            file: enrichBlobAsUploadFile(
                                additionalBlob,
                                conformFilenameToBlobType(imageFilename, additionalBlob),
                            ),
                            activeRenderedImage: activeRenderedImage || undefined,
                        });
                    }
                }
            } finally {
                // With an optimistic preview the element update, the preview reveal, and this callback all
                // run in the same batch, so the modal hands off in the same paint as the board canvas.
                afterSaveCb(true, { viewPreviewReady: hasOptimisticUploadPreview });
            }
        };

        const createCroppedImage = () => {
            const croppedCanvas = cropper.getCroppedCanvas({
                width: sessionImageEditorData.width,
                height: sessionImageEditorData.height,
                maxWidth: 4096,
                maxHeight: 4096,
            });

            if (!croppedCanvas) {
                afterSaveCb(false);
                return;
            }

            croppedCanvas.toBlob(
                (blob: Blob | null) => {
                    if (!blob) {
                        afterSaveCb(false);
                        return;
                    }

                    void blobCreatedCb(blob);
                },
                imageType,
                DEFAULT_LOSSY_IMAGE_QUALITY,
            );
        };

        onSaveStart?.();
        if (scheduleCroppedImageCreation) {
            scheduleCroppedImageCreation(createCroppedImage);
            return;
        }

        createCroppedImage();
    }, [
        cropperRef,
        isDirtyRef,
        elementId,
        cropSourceImage,
        imageType,
        largestUneditedImageUrl,
        hideDrawing,
        afterSaveCb,
        hasOptimisticUploadPreview,
        scheduleCroppedImageCreation,
        getFilenameFromUrl,
        appendToFilename,
        inRange,
        updateElementImageData,
        revertToOriginalImageData,
        uploadEditedImage,
        onSaveStart,
        shouldSaveWhenClean,
        getPersistedImageEditorData,
        getDrawingSessionTransform,
        getRevertedImage,
        createAdditionalCroppedImageBlob,
        uploadAdditionalCroppedImage,
    ]);

    return { handleSave };
};
